/**
 * /english-speaking-doctor-vietnam
 *
 * Service-level landing page targeting "english speaking doctor vietnam"
 * search intent. Aggregates the English-speaking doctor proposition
 * across hotel visit / clinic / telehealth modalities, with coverage map
 * linking back to live + expanding city pages.
 */
import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';
import {
  UrgentTrustStrip, UrgentIntro, UrgentCardGrid, UrgentSteps,
  UrgentFaq, UrgentEditorial, UrgentCtaBand, UrgentCoverageMap,
  CoordinationDisclaimer, UrgentConditionsServices,
} from '../components/UrgentCareSections';
import { ENGLISH_SPEAKING_DOCTOR } from '../data/urgentCareContent';
import { useCatalog } from '../context/CatalogContext';
import { getInfoLandingImage } from '../lib/imageOverrides';

const HERO_IMAGE = 'https://images.unsplash.com/photo-1622253692010-333f2da6031d?auto=format&fit=crop&w=1400&q=80';
const HERO_FALLBACK = 'https://images.pexels.com/photos/4226140/pexels-photo-4226140.jpeg?auto=compress&cs=tinysrgb&w=1400';

const SITE = 'https://vietclinics.com';

const EnglishSpeakingDoctorVietnamPage = () => {
  const c = ENGLISH_SPEAKING_DOCTOR;
  const { overrides } = useCatalog();
  const heroImage = getInfoLandingImage(overrides, 'english-speaking-doctor', HERO_IMAGE);
  const canonical = `${SITE}${c.slug}`;

  const liveCities = [
    { slug: 'hoi-an', name: 'Da Nang & Hoi An', href: '/urgent-medical-care-hoi-an' },
  ];
  const expandingCities = [
    { slug: 'ho-chi-minh', name: 'Ho Chi Minh City', href: '/urgent-medical-care-ho-chi-minh' },
    { slug: 'hanoi',       name: 'Hanoi',             href: '/urgent-medical-care-hanoi' },
    { slug: 'nha-trang',   name: 'Nha Trang',         href: '/urgent-medical-care-nha-trang' },
    { slug: 'phu-quoc',    name: 'Phu Quoc',          href: '/urgent-medical-care-phu-quoc' },
  ];

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Service',
        '@id': `${canonical}#service`,
        serviceType: 'English-speaking doctor coordination',
        provider: { '@type': 'Organization', name: 'VietClinics' },
        areaServed: [{ '@type': 'Country', name: 'Vietnam' }],
        description: c.metaDescription,
        url: canonical,
      },
      {
        '@type': 'WebPage',
        '@id': `${canonical}#webpage`,
        url: canonical,
        name: c.metaTitle,
        description: c.metaDescription,
        inLanguage: 'en',
        dateModified: c.editorial.lastUpdated,
        lastReviewed: c.editorial.lastUpdated,
        reviewedBy: { '@type': 'Organization', name: c.editorial.reviewedBy },
      },
      {
        '@type': 'FAQPage',
        '@id': `${canonical}#faq`,
        mainEntity: c.faq.map((f) => ({
          '@type': 'Question',
          name: f.q,
          acceptedAnswer: { '@type': 'Answer', text: f.a },
        })),
      },
      {
        '@type': 'BreadcrumbList',
        '@id': `${canonical}#breadcrumb`,
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
          { '@type': 'ListItem', position: 2, name: 'Urgent Medical Care', item: `${SITE}/urgent-medical-care` },
          { '@type': 'ListItem', position: 3, name: 'English-Speaking Doctors', item: canonical },
        ],
      },
    ],
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{c.metaTitle}</title>
        <meta name="description" content={c.metaDescription} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={c.metaTitle} />
        <meta property="og:description" content={c.metaDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <Header />

      <section className="relative overflow-hidden bg-white" data-testid="esd-hero">
        <div
          aria-hidden
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(135deg, #F0FAF8 0%, #DCEFEC 60%, #ffffff 100%)',
          }}
        />
        <div className="relative container mx-auto px-4 max-w-7xl py-16 lg:py-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-[11px] font-bold text-[#1A8F8A] uppercase tracking-widest mb-4 inline-block bg-white/80 backdrop-blur px-3 py-1.5 rounded-full border border-[#DCEFEC]">
                {c.hero.eyebrow}
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-slate-900 tracking-tight leading-[1.05] mb-5">
                {c.hero.title}
              </h1>
              <p className="text-lg text-slate-600 leading-relaxed max-w-xl mb-7">
                {c.hero.subtitle}
              </p>
              <div className="max-w-xl">
                <CoordinationDisclaimer testId="esd-disclaimer" />
              </div>
            </div>
            <div className="relative rounded-3xl overflow-hidden bg-slate-100" style={{ aspectRatio: '4 / 3' }}>
              <img
                src={heroImage}
                onError={(e) => { e.currentTarget.src = HERO_FALLBACK; }}
                alt="English-speaking online consultation coordinated by VietClinics"
                className="w-full h-full object-cover"
                loading="eager"
              />
            </div>
          </div>
        </div>
      </section>

      <UrgentTrustStrip />

      <UrgentIntro heading={c.intro.heading} paragraphs={c.intro.paragraphs} testId="esd-intro" />

      <UrgentCardGrid
        eyebrow="What is covered"
        heading="English-speaking doctor — what we coordinate"
        items={c.covered}
        testId="esd-covered"
      />

      <UrgentConditionsServices testId="esd-conditions" />

      <UrgentSteps
        eyebrow="How it works"
        heading="From enquiry to consultation in four steps"
        steps={c.howItWorks}
        testId="esd-steps"
      />

      <UrgentCoverageMap
        liveCities={liveCities}
        expandingCities={expandingCities}
        showServices={false}
        testId="esd-coverage"
      />

      <UrgentFaq
        heading="English-speaking doctors in Vietnam — frequently asked"
        items={c.faq}
        testId="esd-faq"
      />

      <UrgentEditorial editorial={c.editorial} testId="esd-editorial" />

      <UrgentCtaBand
        heading="Need an English-speaking doctor in Vietnam?"
        subheading="Tell us your symptoms and where you are — we will match you with a verified English-speaking clinician and an indicative quote."
        testId="esd-cta"
      />

      <Footer />
    </div>
  );
};

export default EnglishSpeakingDoctorVietnamPage;
